import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { CashFlowService } from '../../services/cash-flow.service';
import { NotificationsService } from '../../services/notifications.service';
import { ReportingService } from '../../services/reporting.service';
import { AppState } from '../app-state/index';
import { selectProjectStatusesByKey } from '../projects/projects.selectors';
import { viewProjectSelectors } from '../view-project/view-project.selectors';
import { cashflowActions } from './cashflow.actions';
import { cashflowSelectors } from './cashflow.selectors';
import { CASHFLOW_TYPES } from '@app/framework/cash-flow/cashflow-table/cashflow.types';
import { PROJECT_STATUS_KEY } from '@app/pages/webapp/projects/projects.interface';

@Injectable()
export class CashflowEffects {
  constructor(
    private store: Store<AppState>,
    private actions: Actions,
    private notif: NotificationsService,
    private cashflowService: CashFlowService,
    private reportingService: ReportingService,
  ) {}

  filtersChanged$ = createEffect(() => {
    return this.actions.pipe(
      ofType(cashflowActions.filtersChanged),
      concatLatestFrom(() => [
        this.store.select(cashflowSelectors.selectCashflowState),
        this.store.select(
          selectProjectStatusesByKey([
            PROJECT_STATUS_KEY.BIDDING,
            PROJECT_STATUS_KEY.AWARDED,
            PROJECT_STATUS_KEY.PLANNED,
            PROJECT_STATUS_KEY.IN_PROGRESS,
            PROJECT_STATUS_KEY.COMPLETED,
          ]),
        ),
      ]),
      switchMap(([_, state, projectStatuses]) => {
        const projectStatusIds = projectStatuses.map((status) => status.id);

        const filters = { ...state.filters, projectStatusIds };

        switch (state.cashflowType) {
          case CASHFLOW_TYPES.budgetVsCommitGeneral:
            return of(cashflowActions.loadBudgetVsCommitGeneral({ filters }));
          case CASHFLOW_TYPES.budgetVsCommitsByProject:
            return of(cashflowActions.loadBudgetVsCommitByProject({ filters }));
          case CASHFLOW_TYPES.budgetVsPrimeGeneral:
            return of(cashflowActions.loadBudgetVsPrimeGeneral({ filters }));
          case CASHFLOW_TYPES.budgetVsPrimesByProject:
            return of(cashflowActions.loadBudgetVsPrimeByProject({ filters }));
          case CASHFLOW_TYPES.primeVsCommitGeneral:
            return of(cashflowActions.loadPrimeVsCommitGeneral({ filters }));
          case CASHFLOW_TYPES.primeVsCommitsByProject:
            return of(cashflowActions.loadPrimeVsCommitByProject({ filters }));
        }
      }),
    );
  });

  loadBudgetVsCommitGeneral$ = createEffect(() => {
    return this.actions.pipe(
      ofType(cashflowActions.loadBudgetVsCommitGeneral),
      switchMap((action) => {
        return this.cashflowService.getCashflowBudgetVsCommit(action.filters).pipe(
          map((budgetVsCommitGeneral) =>
            cashflowActions.budgetVsCommitGeneralLoadedSuccess({ data: budgetVsCommitGeneral }),
          ),
          catchError((error) => {
            this.notif.showError('Failed to load budget vs commit');
            return of(cashflowActions.budgetVsCommitGeneralLoadedError({ error }));
          }),
        );
      }),
    );
  });

  loadBudgetVsCommitByProject$ = createEffect(() => {
    return this.actions.pipe(
      ofType(cashflowActions.loadBudgetVsCommitByProject),
      concatLatestFrom(() => [this.store.select(viewProjectSelectors.selectProjectId)]),
      switchMap(([action, projectId]) => {
        return this.cashflowService
          .getCashflowBudgetVsCommitForProject(projectId, action.filters)
          .pipe(
            map((budgetVsCommitByProject) =>
              cashflowActions.budgetVsCommitByProjectLoadedSuccess({
                projectId,
                data: budgetVsCommitByProject,
              }),
            ),
            catchError((error) => {
              this.notif.showError('Failed to load budget vs commit');
              return of(cashflowActions.budgetVsCommitByProjectLoadedError({ error }));
            }),
          );
      }),
    );
  });

  loadBudgetVsPrimeGeneral$ = createEffect(() => {
    return this.actions.pipe(
      ofType(cashflowActions.loadBudgetVsPrimeGeneral),
      switchMap((action) => {
        return this.cashflowService.getCashflowBudgetVsPrime(action.filters).pipe(
          map((budgetVsPrimeGeneral) =>
            cashflowActions.budgetVsPrimeGeneralLoadedSuccess({ data: budgetVsPrimeGeneral }),
          ),
          catchError((error) => {
            this.notif.showError('Failed to load budget vs prime data');
            return of(cashflowActions.budgetVsPrimeGeneralLoadedError({ error }));
          }),
        );
      }),
    );
  });

  loadBudgetVsPrimeByProject$ = createEffect(() => {
    return this.actions.pipe(
      ofType(cashflowActions.loadBudgetVsPrimeByProject),
      concatLatestFrom(() => [this.store.select(viewProjectSelectors.selectProjectId)]),
      switchMap(([action, projectId]) => {
        return this.cashflowService
          .getCashflowBudgetVsPrimeForProject(projectId, action.filters)
          .pipe(
            map((budgetVsPrimeByProject) =>
              cashflowActions.budgetVsPrimeByProjectLoadedSuccess({
                projectId,
                data: budgetVsPrimeByProject,
              }),
            ),
            catchError((error) => {
              this.notif.showError('Failed to load budget vs prime');
              return of(cashflowActions.budgetVsPrimeByProjectLoadedError({ error }));
            }),
          );
      }),
    );
  });

  loadPrimeVsCommitGeneral$ = createEffect(() => {
    return this.actions.pipe(
      ofType(cashflowActions.loadPrimeVsCommitGeneral),
      switchMap((action) => {
        return this.cashflowService.getCashflowPrimeVsCommit(action.filters).pipe(
          map((primeVsCommitGeneral) =>
            cashflowActions.primeVsCommitGeneralLoadedSuccess({ data: primeVsCommitGeneral }),
          ),
          catchError((error) => {
            this.notif.showError('Failed to load prime vs commit');
            return of(cashflowActions.primeVsCommitGeneralLoadedError({ error }));
          }),
        );
      }),
    );
  });

  loadPrimeVsCommitByProject$ = createEffect(() => {
    return this.actions.pipe(
      ofType(cashflowActions.loadPrimeVsCommitByProject),
      concatLatestFrom(() => [this.store.select(viewProjectSelectors.selectProjectId)]),
      switchMap(([action, projectId]) => {
        return this.cashflowService
          .getCashflowPrimeVsCommitForProject(projectId, action.filters)
          .pipe(
            map((primeVsCommitByProject) =>
              cashflowActions.primeVsCommitByProjectLoadedSuccess({
                projectId,
                data: primeVsCommitByProject,
              }),
            ),
            catchError((error) => {
              this.notif.showError('Failed to load prime vs commit');
              return of(cashflowActions.primeVsCommitByProjectLoadedError({ error }));
            }),
          );
      }),
    );
  });

  updateReportingService$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        cashflowActions.budgetVsCommitGeneralLoadedSuccess,
        cashflowActions.budgetVsCommitByProjectLoadedSuccess,
        cashflowActions.budgetVsPrimeGeneralLoadedSuccess,
        cashflowActions.budgetVsPrimeByProjectLoadedSuccess,
        cashflowActions.primeVsCommitGeneralLoadedSuccess,
        cashflowActions.primeVsCommitByProjectLoadedSuccess,
      ),
      concatLatestFrom(() => [this.store.select(cashflowSelectors.selectCashflowState)]),
      map(([_, state]) => {
        const filters = state.filters;
        const propertyIds = [...new Set(filters.propertyIds)];
        const projectIds = [...new Set(filters.projectIds)];
        this.reportingService.selectedProperty = propertyIds;
        this.reportingService.selectedProjectId = projectIds;

        return cashflowActions.updateReportingSuccess();
      }),
    );
  });
}
